import { useEffect } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import {
  datadogRum,
  DEFAULT_TRACKED_RESOURCE_HEADERS,
  RumInitConfiguration,
} from '@datadog/browser-rum';
import RuntimeEnvironment from 'config/runtime';

const useDatadog = () => {
  const {
    applicationId,
    clientToken,
    enabled,
    environment,
    serviceName,
    proxyUrl
  } = RuntimeEnvironment.datadog;

  const version = process.env.REACT_APP_GIT_REVISION;

  useEffect(() => {
    if (enabled.toString() !== 'true') {
      console.warn(`Datadog not enabled (got ${enabled})`);
      return;
    }

    const warnSetupSkipped = (envVar: string, msg = 'skipping Datadog setup...') => {
      console.warn(`Datadog ${envVar} undefined; ${msg}`);
    };

    if (!clientToken) {
      warnSetupSkipped('clientToken');
      return;
    }

    if (!environment) {
      warnSetupSkipped('environment');
      return;
    }

    datadogLogs.init({
      clientToken: clientToken,
      env: environment,
      forwardErrorsToLogs: false,
      silentMultipleInit: true,
    });
    window.datadogLogs = datadogLogs;

    if (!applicationId){
      warnSetupSkipped('applicationId', 'skipping Datadog RUM setup...');
      return;
    }


    const rumConfig: RumInitConfiguration = {
      applicationId: applicationId,
      clientToken: clientToken,
      defaultPrivacyLevel: 'allow',
      env: environment,
      // The "x" before the query string is arbitrary and used to confuse ad-blockers
      // See: https://docs.datadoghq.com/real_user_monitoring/guide/proxy-rum-data/?tab=npm
      // And: https://github.com/easylist/easylist/blob/997fb6533c719a015c21723b34e0cedefcc0d83d/easyprivacy/easyprivacy_general.txt#L3840
      proxy: (options: { path: string; parameters: string}) => `${proxyUrl}${options.path}x?${options.parameters}`,
      service: serviceName,
      sessionReplaySampleRate: 100,
      sessionSampleRate: 100,
      site: 'datadoghq.com',
      traceSampleRate: 100,
      trackLongTasks: true,
      trackResources: true,
      // SDK >= 7.7 (beta): emits websocket-connecting/closed with code+reason, including handshakes
      // that never opened — the wss:// blind spot contently-issues#111 called structural (ka#60403803).
      betaTrackWebSockets: true,
      // Capture request/response HEADERS on resource events (SDK >= 6.33, GA 7.1;
      // off by default). Closes the "I needed to see the headers" slice of HAR
      // requests. `true` would capture only the 10 DEFAULT_TRACKED_RESOURCE_HEADERS
      // (content-type, cache-control, etag, ...) which omit `x-request-id` — the
      // one header that lets a RUM resource pivot to the Rails request_id in our
      // logs (config.log_tags). So we extend the defaults with x-request-id. The
      // SDK still drops sensitive headers (authorization, cookie, token, api-key,
      // ...) by name regardless of this list, so no credential leak. Bodies are
      // never captured.
      trackResourceHeaders: [
        ...DEFAULT_TRACKED_RESOURCE_HEADERS.map((name) => ({ name })),
        { name: 'x-request-id' },
      ],
      trackUserInteractions: true,
      version: version,
      // v7 defaults this to true, which adds a `baggage` header to every TRACED
      // request (the *.contently.com calls the allowedTracingUrls below now trace).
      // Pin false to preserve v5 behavior: if a backend's CORS
      // Access-Control-Allow-Headers does not list `baggage`, those cross-origin
      // traced calls would fail preflight. Re-enable deliberately once backends
      // advertise the header.
      propagateTraceBaggage: false,
      // Inject distributed-trace headers on calls to our backends so RUM resources
      // join to the `platform` APM trace. Exact-string entries only match that exact
      // origin, so the prod API host is supplied from runtime config and `*.contently.com`
      // subdomains (api., GraphQL, portfolio subdomains) are covered by an explicit regex.
      allowedTracingUrls: [
        RuntimeEnvironment.platform.host,
        'https://contently.com',
        /https:\/\/.*\.contently\.com/,
        'https://snuffl.in',
        /https:\/\/.*\.contently\.xyz/,
        /https:\/\/.*\.contently\.systems/,
      ],
    };

    datadogRum.init(rumConfig);

    // Identify the authenticated user for session replays and error attribution.
    // User info is injected into the SPA HTML by PlatformWebController#show.
    // id-only by design: parity with the monolith and PII hygiene — name/email are
    // not sent to Datadog. Look a session up by @usr.id, then cross-reference internally.
    const currentUser = (window as any).__CURRENT_USER__;
    if (currentUser) {
      datadogRum.setUser({
        id: String(currentUser.id),
      });
    }

    datadogRum.startSessionReplayRecording();
  }, [
    applicationId,
    clientToken,
    enabled,
    environment,
    serviceName,
    version,
    proxyUrl
  ]);
};

export default useDatadog;
